import { isRejectedWithValue } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../utils/apiError';

// Endpoints that should NEVER produce a global toast
// (login failures are shown inline; auth/me 401 triggers refresh; mutations that have their own UI handling)
const SILENT_ENDPOINTS = new Set([
  'login',
  'refresh',
  'me',
  // Modal-driven mutations that handle errors themselves with a banner:
  'createUser', 'updateUser', 'updateUserRoles',
  'createLead', 'updateLead',
  'createPatient',
  'createAppointment',
  'sendMessage',
  'createInvoice', 'addPayment',
]);

/**
 * Global RTK Query error logger.
 *
 * If a mutation/query failed and no modal handled it, we still want the user
 * to see something. We surface a toast with the parsed backend message.
 */
export const rtkQueryErrorLogger = (api) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const endpoint = action.meta?.arg?.endpointName;
    const status = action.payload?.status;

    // Skip silent endpoints (UI handles them inline)
    if (endpoint && SILENT_ENDPOINTS.has(endpoint)) return next(action);

    // Skip 401 — handled by baseApi (refresh + logout)
    if (status === 401) return next(action);

    // Log to console for developers
    // eslint-disable-next-line no-console
    console.warn('[API error]', endpoint, status, action.payload);

    const msg = apiErrorMessage(action.payload);
    if (msg) toast.error(msg, { toastId: `api-err-${endpoint ?? 'unknown'}` });
  }
  return next(action);
};
